import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { TESTIMONIALS } from "@/data/site";
import { Kicker } from "./Reveal";

export default function Testimonials() {
  const { t, lang } = useLang();
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const count = TESTIMONIALS.length;

  const go = useCallback((d) => {
    setDir(d);
    setIndex((i) => (i + d + count) % count);
  }, [count]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => go(1), 6000);
    return () => clearInterval(id);
  }, [paused, go]);

  const item = TESTIMONIALS[index];

  return (
    <section
      className="relative overflow-hidden bg-[var(--bg-2)] py-24 md:py-36"
      data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="pointer-events-none absolute inset-0 opacity-30 [background:radial-gradient(600px_400px_at_50%_0%,rgba(212,175,55,0.12),transparent)]" />

      <div className="relative mx-auto max-w-4xl px-5 text-center md:px-10">
        <Kicker className="mb-6 justify-center">{t.testiKicker}</Kicker>
        <h2 className="font-serif text-4xl leading-[0.98] tracking-tight text-[var(--text)] md:text-6xl">
          {t.testiTitle} <span className="italic gold-gradient-text">{t.testiTitleEm}</span>
        </h2>

        <Quote className="mx-auto mt-12 h-10 w-10 text-[var(--gold)]/40" />

        <div className="relative mt-6 min-h-[220px] md:min-h-[200px]">
          <AnimatePresence mode="wait" custom={dir}>
            <motion.figure
              key={index}
              custom={dir}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="testimonial-card"
            >
              <blockquote className="mx-auto max-w-3xl font-serif text-2xl italic leading-snug text-[var(--text)] md:text-[32px] md:leading-[1.3]">
                “{lang === "fr" ? item.fr : item.en}”
              </blockquote>

              <div className="mt-8 flex items-center justify-center gap-1" aria-label={`${item.stars} / 5`}>
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star
                    key={s}
                    size={16}
                    className={s < item.stars ? "fill-[var(--gold)] text-[var(--gold)]" : "text-[var(--text-3)]"}
                  />
                ))}
              </div>

              <figcaption className="mt-4">
                <span className="font-serif text-xl text-[var(--gold-light)]">{item.name}</span>
                <span className="mt-1 block font-sans text-[11px] uppercase tracking-[0.24em] text-[var(--text-3)]">
                  {item.role}
                </span>
              </figcaption>
            </motion.figure>
          </AnimatePresence>
        </div>

        {/* Controls */}
        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            onClick={() => go(-1)}
            data-testid="testi-prev"
            aria-label="Précédent"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--gold)]/40 text-[var(--gold)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--bg)]"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="flex items-center gap-2.5">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
                data-testid={`testi-dot-${i}`}
                aria-label={`Avis ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === index ? "w-7 bg-[var(--gold)]" : "w-1.5 bg-[var(--text-3)] hover:bg-[var(--gold)]/50"
                }`}
              />
            ))}
          </div>

          <button
            onClick={() => go(1)}
            data-testid="testi-next"
            aria-label="Suivant"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--gold)]/40 text-[var(--gold)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--bg)]"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
